import React from 'react';
import { Plus, X, Star } from 'lucide-react';

const ReviewSchemaForm = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleReviewAdd = () => {
    const items = data?.items || [];
    items?.push({
      authorName: '',
      rating: 5,
      text: '',
      date: new Date()?.toISOString()?.split('T')?.[0]
    });
    handleChange('items', items);
  };

  const handleReviewRemove = (index) => {
    const items = [...(data?.items || [])];
    items?.splice(index, 1);
    handleChange('items', items);
  };

  const handleReviewChange = (index, field, value) => {
    const items = [...(data?.items || [])];
    items[index] = { ...items?.[index], [field]: value };
    handleChange('items', items);
  };

  const renderStars = (rating, onChange) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none"
          >
            <Star
              className={`w-6 h-6 ${
                star <= rating
                  ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Average Rating <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.1"
            min="1"
            max="5"
            value={data?.averageRating || ''}
            onChange={(e) => handleChange('averageRating', e?.target?.value)}
            placeholder="4.8"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Reviews <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={data?.totalReviews || ''}
            onChange={(e) => handleChange('totalReviews', e?.target?.value)}
            placeholder="100"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
          />
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Individual Reviews</h3>
          <button
            type="button"
            onClick={handleReviewAdd}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Review
          </button>
        </div>

        {(data?.items || [])?.map((review, index) => (
          <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">Review {index + 1}</h4>
              <button
                type="button"
                onClick={() => handleReviewRemove(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author Name
                </label>
                <input
                  type="text"
                  value={review?.authorName || ''}
                  onChange={(e) => handleReviewChange(index, 'authorName', e?.target?.value)}
                  placeholder="John D."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                {renderStars(review?.rating || 5, (rating) =>
                  handleReviewChange(index, 'rating', rating)
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Date
                </label>
                <input
                  type="date"
                  value={review?.date || ''}
                  onChange={(e) => handleReviewChange(index, 'date', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Text
                </label>
                <textarea
                  value={review?.text || ''}
                  onChange={(e) => handleReviewChange(index, 'text', e?.target?.value)}
                  placeholder="Excellent service! They helped us through a difficult time with professionalism and care."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>Important:</strong> Only include genuine client reviews. Review schema markup must represent actual customer feedback to comply with search engine guidelines.
        </p>
      </div>
    </div>
  );
};

export default ReviewSchemaForm;